import React from "react";
import { Presentation } from "lucide-react";
import { Tooltip } from "@mui/material";

export const DetailPanel = ({ data }) => {
  const firstValue = Object.values(data ?? {})[0];

  if (!firstValue) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        No data available
      </div>
    );
  }

  const format = (s) =>
    s.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

  return (
    <div className="h-full overflow-y-auto bg-[#F7F8FA]">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 w-full bg-[#F7F8FA] shadow-sm">
        <div className="bg-[#dedfe557] px-[24px] py-[20px]">
          <h3 className="mb-[8px] text-[15px] font-semibold text-gray-900">
            {firstValue.title}
          </h3>
          <p className="text-sm leading-relaxed text-gray-600">
            {firstValue.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white px-[24px] py-[20px]">
        {/* KPI Components */}
        <div className="mb-[32px]">
          <h3 className="mb-[16px] text-[15px] font-semibold text-gray-900">
            KPI Components
          </h3>

          {firstValue?.kpiComponents?.length > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-[16px]">
              {firstValue.kpiComponents.map((group, index) => (
                <div
                  key={index}
                  className="rounded-[12px] border border-gray-200 bg-white p-[16px] shadow-sm"
                >
                  <div className="mb-[12px] text-[11px] font-semibold uppercase tracking-wider text-[#FF03F3]">
                    {group.source}
                  </div>

                  {group.source === "Table" &&
                    group.tables?.map((item, idx) => (
                      <div
                        key={idx}
                        className="mb-[8px] rounded-[8px] border border-gray-200 bg-[#F7F8FA] px-[12px] py-[8px]"
                      >
                        <div className="text-[11px] font-medium text-gray-900">
                          {item.tableName}
                        </div>
                        {item.columns?.length > 0 && (
                          <div className="mt-[4px] text-[12px] text-gray-500">
                            Columns: {item.columns.join(", ")}
                          </div>
                        )}
                      </div>
                    ))}

                  {group.source === "Measure" &&
                    group.measures?.map((item, idx) => (
                      <div
                        key={idx}
                        className="mb-[8px] rounded-[8px] border border-gray-200 bg-[#F7F8FA] px-[12px] py-[8px]"
                      >
                        <div className="text-[11px] font-medium text-gray-900">
                          {item.measureName}
                        </div>
                      </div>
                    ))}

                  {!group.tables?.length && !group.measures?.length && (
                    <div className="text-[11px] text-gray-500">
                      No data available
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">No data available</div>
          )}
        </div>

        {/* Where to find */}
        <div className="mb-[32px]">
          <h3 className="mb-[16px] text-[15px] font-semibold text-gray-900">
            Where to find
          </h3>

          {firstValue?.whereToFindData?.length > 0 ? (
            <div className="grid grid-cols-1 gap-[16px] sm:grid-cols-2 lg:grid-cols-3">
              {firstValue.whereToFindData.map((item, index) => {
                const types = Array.isArray(item.type) ? item.type : [];
                const [first, ...rest] = types;

                return (
                  <div
                    key={index}
                    className="flex gap-[12px] rounded-[10px] border border-gray-200 bg-white p-[16px] transition hover:shadow-sm"
                  >
                    <span
                      className="mt-[6px] shrink-0 rounded-full bg-[#475bcc]"
                      style={{ width: "10px", height: "10px" }}
                    />

                    <div className="min-w-0 flex-1">
                      <Tooltip title={item.pageName} arrow>
                        <div className="cursor-pointer truncate text-[11px] font-medium text-gray-900">
                          {item.pageName}
                        </div>
                      </Tooltip>

                      <div className="mt-[6px] flex items-center gap-[8px] text-[11px] text-gray-600">
                        <Presentation className="h-[18px] w-[18px]" />

                        {first && <span>{format(first)}</span>}

                        {rest.length > 0 && (
                          <Tooltip
                            title={
                              <div className="flex flex-col gap-[4px]">
                                {rest.map((t, i) => (
                                  <span key={i}>{format(t)}</span>
                                ))}
                              </div>
                            }
                            arrow
                            placement="top"
                          >
                            <span
                              className="inline-block cursor-pointer font-medium underline"
                              style={{ color: "#FF03F3" }}
                            >
                              +{rest.length}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">No data available</div>
          )}
        </div>

        {/* DAX Calculation */}
        <div>
          <h3 className="mb-[12px] text-[15px] font-semibold text-gray-900">
            DAX Calculation Logic
          </h3>

          <pre className="overflow-x-auto rounded-[10px] bg-[#0F172A] p-[16px] text-[11px] leading-relaxed text-gray-100">
            <code>{firstValue.calculation || "No calculation available"}</code>
          </pre>
        </div>
      </div>
    </div>
  );
};
